import type { ChartExcelRow } from './chartExcel';
import type { MapDataItem } from '../schemas/statsSchema';

/**
 * Nivo `ResponsiveBar` with `layout="horizontal"` renders the LAST array element at the TOP
 * of the axis. Every horizontal-bar chart in this app sorts ascending (or reverse-
 * alphabetically) on purpose so the largest value lands on top. This helper reproduces a
 * chart's own sort, then reverses, so spreadsheet row 1 = the chart's top bar.
 *
 * Do not use for vertical bars or pies — those read top-to-bottom naturally and must NOT be
 * reversed.
 */
export function buildRankedBarRows<T>(
	items: T[],
	sortComparator: (a: T, b: T) => number,
	mapRow: (item: T) => ChartExcelRow,
): ChartExcelRow[] {
	return [...items].sort(sortComparator).reverse().map(mapRow);
}

/** AdmissionMethodsPieChart — sorted descending by studentCount, matching the rendered pie. */
export function buildAdmissionMethodRows(
	admissionMethods: Array<{ methodCode: string; methodName: string; studentCount: number }>,
): ChartExcelRow[] {
	return [...admissionMethods]
		.sort((a, b) => b.studentCount - a.studentCount)
		.map((m) => ({
			'Mã PT': m.methodCode,
			'Tên phương thức': m.methodName,
			'Số lượng SV': m.studentCount,
		}));
}

/**
 * FacultyStatsChart — reversed (horizontal bar, sorted `b.label.localeCompare(a.label)` at
 * `sortedData` memo). `entityNoun` follows the Khoa <-> Ngành header switch driven by the
 * global `selectedFaculty`.
 */
export function buildFacultyStatsRows(
	data: Array<{ facultyCode: string; facultyName?: string; studentCount: number }>,
	entityNoun: 'khoa' | 'ngành' = 'khoa',
): ChartExcelRow[] {
	const items = data.map((f) => ({
		code: f.facultyCode,
		name: f.facultyName || f.facultyCode,
		count: f.studentCount,
	}));
	return buildRankedBarRows(
		items,
		(a, b) => b.name.localeCompare(a.name),
		(item) => ({
			[`Mã ${entityNoun}`]: item.code,
			[`Tên ${entityNoun}`]: item.name,
			'Số lượng SV': item.count,
		}),
	);
}

/**
 * Grade12ScoreDistributionChart — one row per (subject, range), covering every subject the
 * chart's small-multiples grid renders (`Môn` column disambiguates rows across subjects).
 * Vertical layout per panel: no reversal.
 */
export function buildGrade12ScoreRows(
	scoreDistribution?: Array<{ subject: string; ranges: Array<{ range: string; count: number }> }>,
): ChartExcelRow[] {
	if (!scoreDistribution || scoreDistribution.length === 0) {
		return [];
	}
	// One row per (subject, range) pair, tagged with a `Môn` column. A subject with an empty
	// `ranges` array (no score data) contributes zero rows via flatMap — it does not need a
	// separate guard, matching the chart's own partial-data handling.
	return scoreDistribution.flatMap((subject) =>
		subject.ranges.map((r) => ({
			'Môn': subject.subject,
			'Khoảng điểm': r.range,
			'Số lượng SV': r.count,
		})),
	);
}

/**
 * GenderDistributionChart bar branch — reversed (horizontal bar, sorted
 * `b.label.localeCompare(a.label)` in its `barData` memo). `entityLabel` follows the
 * Khoa <-> Ngành header switch.
 */
export function buildGenderBarRows(
	data: Array<
		{ maleCount: number; femaleCount: number } & ({ facultyName: string } | { majorName: string })
	>,
	entityLabel: 'Khoa' | 'Ngành' = 'Khoa',
): ChartExcelRow[] {
	const items = data.map((item) => ({
		label: 'facultyName' in item ? item.facultyName : item.majorName,
		nam: item.maleCount,
		nu: item.femaleCount,
	}));
	return buildRankedBarRows(
		items,
		(a, b) => b.label.localeCompare(a.label),
		(item) => ({
			[entityLabel]: item.label,
			'Nam': item.nam,
			'Nữ': item.nu,
		}),
	);
}

/**
 * GenderDistributionChart pie branch — a clean `Giới tính` label plus the numeric count, not
 * the decorated `label` string that embeds a percentage. Not reversed (pie). Mirrors the
 * component's own "no total -> no rows" branch.
 */
export function buildGenderPieRows(
	data: Array<{ maleCount: number; femaleCount: number }>,
): ChartExcelRow[] {
	const totalMale = data.reduce((sum, item) => sum + item.maleCount, 0);
	const totalFemale = data.reduce((sum, item) => sum + item.femaleCount, 0);
	if (totalMale + totalFemale === 0) {
		return [];
	}
	return [
		{ 'Giới tính': 'Nam', 'Số lượng': totalMale },
		{ 'Giới tính': 'Nữ', 'Số lượng': totalFemale },
	];
}

/**
 * RemainingQuotaChart — reversed (horizontal bar).
 *
 * The comparator below deliberately MIRRORS the component's own
 * `b.label.localeCompare(a.label)` sort, where `label` is `item.name`. It cannot simply reuse
 * the component's `chartData` memo: that memo drops the quota fields this export needs, and
 * re-pairing rows by name would break on duplicate names. Sorting and row-shaping therefore
 * happen in one pass here, so order is correct by construction.
 *
 * If that component's sort ever changes, change this one too — the export order would
 * otherwise stop matching the chart, silently.
 */
export function buildQuotaRows(
	data: Array<{
		code: string;
		name: string;
		totalQuota: number;
		enrollmentCount: number;
		remainingQuota: number;
	}>,
): ChartExcelRow[] {
	return buildRankedBarRows(
		data,
		(a, b) => b.name.localeCompare(a.name),
		(item) => ({
			'Mã': item.code,
			'Tên': item.name,
			'Tổng chỉ tiêu': item.totalQuota,
			'Đã tuyển': item.enrollmentCount,
			'Còn lại': item.remainingQuota,
		}),
	);
}

/**
 * Top10ProvincesBeforeMergeChart / Top10ProvincesAfterMergeChart — reversed (horizontal bar,
 * sorted ascending by studentCount at :66 / :67). Both charts share this identical row
 * shape, so one builder covers both (DRY) — `Top10ProvincesChart.tsx` is dead code and gets
 * no builder.
 */
export function buildTopProvincesRows(
	data: Array<{ provinceName: string; studentCount: number }>,
): ChartExcelRow[] {
	return buildRankedBarRows(
		data,
		(a, b) => a.studentCount - b.studentCount,
		(p) => ({ 'Tỉnh/Thành phố': p.provinceName, 'Số lượng SV': p.studentCount }),
	);
}

/**
 * TopSchoolsChart / TopSchoolsOverallChart — reversed (horizontal bar, sorted ascending by
 * studentCount in their `barData` memos). Callers
 * must pass the already top-10-cut list for that render site (each `TopSchoolsChart` loop
 * iteration has its own local `.slice(0, 10)` — do not hoist one call out of the `.map()`).
 */
export function buildTopSchoolsRows(
	schools: Array<{ schoolName: string; studentCount: number }>,
): ChartExcelRow[] {
	return buildRankedBarRows(
		schools,
		(a, b) => a.studentCount - b.studentCount,
		(s) => ({ 'Trường THPT': s.schoolName, 'Số lượng SV': s.studentCount }),
	);
}

export interface HeatmapDataItem {
	id: string;
	data: Array<{ x: string; y: number }>;
}

/**
 * ProvinceByFacultyHeatmap / ProvinceByMajorHeatmap (shared, DRY) — pivots the Nivo heatmap
 * matrix to one row per province with one column per `x` (khoa or ngành name). Missing
 * cells are `null`, NOT `0` — the chart renders absent cells with `emptyColor`, and `0` would
 * misrepresent "no data" as "zero students". Matrix layout: no reversal.
 */
export function pivotHeatmapRows(data: HeatmapDataItem[]): ChartExcelRow[] {
	const columns: string[] = [];
	const seenColumns = new Set<string>();
	for (const item of data) {
		for (const cell of item.data) {
			if (!seenColumns.has(cell.x)) {
				seenColumns.add(cell.x);
				columns.push(cell.x);
			}
		}
	}

	return data.map((item) => {
		const valueByColumn = new Map(item.data.map((cell) => [cell.x, cell.y]));
		const row: ChartExcelRow = { 'Tỉnh/Thành phố': item.id };
		for (const column of columns) {
			row[column] = valueByColumn.has(column) ? (valueByColumn.get(column) as number) : null;
		}
		return row;
	});
}

/**
 * BaseChoroplethMap — rows from `chartData_echarts` for the map's own selected year (not the
 * dashboard's — the map owns its year independently). Matrix/map layout: no reversal.
 */
export function buildChoroplethRows(data: MapDataItem[]): ChartExcelRow[] {
	return data.map((item) => ({
		'Tỉnh/Thành phố': item.tenTinh ?? item.tinh ?? item.name ?? '',
		'Số lượng SV': item.value ?? 0,
	}));
}
